// @flow

import React, { useEffect, useState } from "react";
import { localize } from "redux-i18n";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Input,
  View,
  Label,
  FormGroup,
  GSGrid,
} from "@components";
import type {
  ShiftPattern,
  UnassignReassignShiftPatternModalType,
  UnassignReassignShiftPatternCauseType,
  EmployeePositionsAssignedToShiftPattern,
  DepartmentsAssignedToShiftPattern,
} from "@modules/service/reducer/shift-patterns/types";
import {
  UnassignReassignShiftPatternTypeEnum,
  UnassignReassignShiftPatternCauseEnum,
} from "@modules/service/constants";
import { ReassignItemsToShiftPatternModal } from "../";

type OwnProps = $Exact<{
  isOpen: boolean,
  closeModal: Function,
  shiftPattern: ?ShiftPattern,
  modalType: ?UnassignReassignShiftPatternModalType,
  employees: ?Array<EmployeePositionsAssignedToShiftPattern>,
  isReassignItemsToShiftPatternModalVisible: boolean,
  toggleUnassignReassignItemsToShiftPatternModal: Function,
  departments: ?Array<DepartmentsAssignedToShiftPattern>,
  unassignReassignItemsShiftPattern: Function,
  clientId: ?number,
  shiftPatternsList: ShiftPattern[],
}>;

type ComponentState = $Exact<{
  selected: number[],
  cause: UnassignReassignShiftPatternCauseType,
}>;

type Props = {
  ...PropsFromLocalize,
  ...OwnProps,
};

export function UnassignReassignShiftPatternModal(props: Props) {
  const [selected, setSelected] = useState([]);
  const [cause, setCause] = useState(null);
  const [
    isReassignItemsToShiftPatternModalVisible,
    setIsReassignItemsToShiftPatternModalVisible,
  ] = useState(false);

  function onToggleReassignItemsToShiftPatternModal() {
    setIsReassignItemsToShiftPatternModalVisible(!isReassignItemsToShiftPatternModalVisible);
  }

  function onUnassignReassignButtonlick(cause: UnassignReassignShiftPatternCauseType) {
    return function() {
      setCause(cause);
      onToggleReassignItemsToShiftPatternModal();
    };
  }

  function modalContent(): Object {
    const shiftPatternName = (props.shiftPattern || {}).name;
    return props.modalType === UnassignReassignShiftPatternTypeEnum.EMPLOYEES
      ? {
          modalTitle: props.t("EMPLOYEES_FOR_PATTERN", {
            patternName: shiftPatternName,
          }),
          tableData: (props.employees || []).map(employee => {
            return {
              ...employee,
              fullName: `${employee.firstName} ${employee.lastName}`,
              department: employee.organisationalUnit.name,
            };
          }),
          tableCols: [
            { key: "employeeReference", name: props.t("EMPLOYEE_REF"), align: "left" },
            { key: "fullName", name: props.t("EMPLOYEE_NAME") },
            { key: "department", name: props.t("DEPARTMENT") },
          ],
          reassignModalName: props.t("REASSIGN_EMPLOYEES"),
          reassignModalDescription: props.t(
            [
              "REASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN",
              "REASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN_plural",
              "employeesCount",
            ],
            {
              employeesCount: selected.length,
              shiftPatternName,
            },
          ),
          unassignModalName: props.t("UNASSIGN_EMPLOYEES"),
          unassignModalDescription: props.t(
            [
              "UNASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN",
              "UNASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN_plural",
              "employeesCount",
            ],
            {
              employeesCount: selected.length,
              shiftPatternName,
            },
          ),
        }
      : {
          modalTitle: props.t("DEPARTMENTS_FOR_PATTERN", {
            patternName: shiftPatternName,
          }),
          tableData: props.departments,
          tableCols: departmentCols,
          reassignModalName: props.t("REASSIGN_DEPARTMENTS"),
          reassignModalDescription: props.t(
            [
              "REASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN",
              "REASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN_plural",
              "departmentsCount",
            ],
            {
              departmentsCount: selected.length,
              shiftPatternName,
            },
          ),
          unassignModalName: props.t("UNASSIGN_DEPARTMENTS"),
          unassignModalDescription: props.t(
            [
              "UNASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN",
              "UNASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN_plural",
              "departmentsCount",
            ],
            {
              departmentsCount: selected.length,
              shiftPatternName,
            },
          ),
        };
  }

  const departmentCols = [
    { key: "id", name: props.t("DEPARTMENT_REF"), width: 140 },
    { key: "name", name: props.t("DEPARTMENT") },
  ];

  function onSelect(selectedItems: number[]) {
    setSelected(selectedItems);
  }

  function actionHandler(newShiftPatternId: ?number) {
    return function() {
      const update = newShiftPatternId ? { newShiftPatternId } : {};
      props.unassignReassignItemsShiftPattern({
        clientId: props.clientId,
        successMessage: newShiftPatternId ? "SUCCESSFULLY_REASSIGNED" : "SUCCESSFULLY_UNASSIGNED",
        data: {
          ids: [...selected],
          ...update,
        },
      });
      setSelected([]);
    };
  }

  return (
    <View>
      {props.isOpen && (
        <Modal className="modal-large">
          <ModalHeader toggle={props.closeModal} className="d-flex justify-content-between">
            {modalContent().modalTitle}
          </ModalHeader>
          <ModalBody>
            <View>
              <FormGroup>
                <Label for="shiftPattern">{props.t("SHIFT_PATTERN_FIELD_LABEL_NAME")}</Label>
                <Input id="shiftPattern" readOnly value={(props.shiftPattern || {}).name} />
              </FormGroup>
            </View>
            <GSGrid
              rows={modalContent().tableData}
              columns={modalContent().tableCols}
              selectedRows={selected}
              onSelectedRowsChange={onSelect}
            />
          </ModalBody>
          <ModalFooter className="d-flex justify-content-end gap-1">
            <Button color="secondary" onClick={props.closeModal}>
              {props.t("CANCEL")}
            </Button>
            <Button
              onClick={onUnassignReassignButtonlick(UnassignReassignShiftPatternCauseEnum.UNASSIGN)}
              disabled={!selected.length}
            >
              {props.t("UNASSIGN_SELECTED")}
            </Button>
            {props.shiftPatternsList.length > 1 ? (
              <Button
                onClick={onUnassignReassignButtonlick(
                  UnassignReassignShiftPatternCauseEnum.REASSIGN,
                )}
                disabled={!selected.length}
              >
                {props.t("REASSIGN_SELECTED")}
              </Button>
            ) : null}
          </ModalFooter>
        </Modal>
      )}

      {isReassignItemsToShiftPatternModalVisible && (
        <ReassignItemsToShiftPatternModal
          modalTitle={
            cause === UnassignReassignShiftPatternCauseEnum.UNASSIGN
              ? modalContent().unassignModalName
              : modalContent().reassignModalName
          }
          modalDescription={
            cause === UnassignReassignShiftPatternCauseEnum.UNASSIGN
              ? modalContent().unassignModalDescription
              : modalContent().reassignModalDescription
          }
          toggleModal={onToggleReassignItemsToShiftPatternModal}
          cause={cause}
          actionHandler={actionHandler}
          selectedShiftPatternId={(props.shiftPattern || {}).id}
          shiftPatternsList={props.shiftPatternsList}
        />
      )}
    </View>
  );
}

export default localize()(UnassignReassignShiftPatternModal);
